import { useState, useRef } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { publicRequest } from "../requestMethods";
import theme from "../theme";
import { mobile } from "../responsive";
import { IconX, IconCamera, IconPaperclip, IconCheck } from "../icons";
import { useToast } from "../ToastContext";

const Card = styled.div`
  background: ${theme.cardBg};
  border-radius: 10px;
  border: 1.5px solid ${theme.cardBorder};
  box-shadow: 0 2px 12px rgba(26, 18, 8, 0.06);
  padding: 22px 24px 20px;
  position: relative;
  ${mobile(`padding: 16px 14px;`)}
`;

const CardTitle = styled.h2`
  font-family: var(--font-display, 'Georgia', serif);
  font-size: 18px;
  font-weight: 400;
  color: ${theme.textMain};
  margin: 0 0 16px;
  letter-spacing: -0.3px;
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 14px;
  right: 14px;
  background: transparent;
  border: 1.5px solid ${theme.cardBorder};
  font-size: 14px;
  cursor: pointer;
  color: ${theme.textMuted};
  line-height: 1;
  padding: 4px 7px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  transition: color 0.15s, background 0.15s, border-color 0.15s;
  &:hover { color: ${theme.textMain}; background: ${theme.bg}; border-color: ${theme.textMuted}; }
`;

const Label = styled.label`
  display: block;
  font-size: 11px;
  font-weight: 700;
  color: ${theme.textMuted};
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.8px;
`;

const Input = styled.input`
  width: 100%;
  background: ${theme.bg};
  border: 1.5px solid ${theme.cardBorder};
  border-radius: 7px;
  padding: 8px 12px;
  font-size: 13.5px;
  font-family: inherit;
  color: ${theme.textMain};
  outline: none;
  margin-bottom: 12px;
  box-sizing: border-box;
  transition: border-color 0.15s, box-shadow 0.15s;
  &::placeholder { color: ${theme.textMuted}; }
  &:focus {
    border-color: ${theme.primary};
    box-shadow: 0 0 0 3px ${theme.primaryGlow};
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  background: ${theme.bg};
  border: 1.5px solid ${theme.cardBorder};
  border-radius: 7px;
  padding: 8px 12px;
  font-size: 13.5px;
  font-family: inherit;
  color: ${theme.textMain};
  outline: none;
  margin-bottom: 12px;
  resize: none;
  height: 64px;
  box-sizing: border-box;
  transition: border-color 0.15s, box-shadow 0.15s;
  &::placeholder { color: ${theme.textMuted}; }
  &:focus {
    border-color: ${theme.primary};
    box-shadow: 0 0 0 3px ${theme.primaryGlow};
  }
`;

const FileRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 14px;
  ${mobile(`grid-template-columns: 1fr;`)}
`;

const FilePickerGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const HiddenInput = styled.input`
  display: none;
`;

const FilePickerBtn = styled.button`
  background: ${(p) => (p.$dragging ? theme.primaryLight : theme.bg)};
  border: 1.5px dashed ${(p) => (p.$hasFile || p.$dragging ? theme.primary : theme.cardBorder)};
  border-radius: 7px;
  padding: 9px 12px;
  font-size: 12px;
  font-weight: 600;
  font-family: inherit;
  color: ${(p) => (p.$hasFile || p.$dragging ? theme.primary : theme.textMuted)};
  cursor: pointer;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: border-color 0.15s, color 0.15s, background 0.15s;
  &:hover {
    border-color: ${theme.primary};
    color: ${theme.primary};
    background: ${theme.primaryLight};
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  background: ${(p) => (p.disabled ? theme.cardBorder : theme.textMain)};
  color: ${theme.cardBg};
  border: none;
  border-radius: 8px;
  padding: 11px;
  font-size: 14px;
  font-weight: 600;
  font-family: inherit;
  cursor: ${(p) => (p.disabled ? "not-allowed" : "pointer")};
  transition: background 0.2s, transform 0.1s;
  &:hover:not(:disabled) {
    background: ${theme.primary};
    transform: translateY(-1px);
  }
`;

const StatusMsg = styled.p`
  font-size: 13px;
  color: ${(p) => (p.$error ? theme.danger : theme.success)};
  margin: 10px 0 0;
  text-align: center;
  font-weight: 500;
`;

export default function UploadNote({ onNoteCreated, onCancel }) {
  const currentUser = useSelector((state) => state.user.currentUser);
  const showToast = useToast();
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [thumbnail, setThumbnail] = useState(null);
  const [noteFile, setNoteFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const thumbRef = useRef();
  const fileRef = useRef();
  const [thumbDragging, setThumbDragging] = useState(false);
  const [fileDragging, setFileDragging] = useState(false);

  const MAX_THUMB_SIZE = 5 * 1024 * 1024;   // 5 MB
  const MAX_FILE_SIZE  = 20 * 1024 * 1024;  // 20 MB
  const ALLOWED_NOTE_EXTS = [".pdf", ".doc", ".docx", ".ppt", ".pptx", ".txt"];

  const handleThumbnailChange = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      showToast("Thumbnail must be an image (jpg, png, gif…)", 4000, "error");
      return;
    }
    if (file.size > MAX_THUMB_SIZE) {
      showToast("Thumbnail is too large. Max size is 5 MB.", 4000, "error");
      return;
    }
    setThumbnail(file);
    showToast(`Thumbnail set: ${file.name}`, 2500);
  };

  const handleNoteFileChange = (file) => {
    if (!file) return;
    const ext = "." + file.name.split(".").pop().toLowerCase();
    if (!ALLOWED_NOTE_EXTS.includes(ext)) {
      showToast(`Invalid file type. Allowed: PDF, DOC, DOCX, PPT, PPTX, TXT`, 4000, "error");
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      showToast("File is too large. Max size is 20 MB.", 4000, "error");
      return;
    }
    setNoteFile(file);
    showToast(`File set: ${file.name}`, 2500);
  };

  const makeDragHandlers = (type, setDragging) => ({
    onDragOver: (e) => { e.preventDefault(); setDragging(true); },
    onDragLeave: () => setDragging(false),
    onDrop: (e) => {
      e.preventDefault();
      setDragging(false);
      const file = e.dataTransfer.files[0];
      if (!file) return;
      if (type === "image") handleThumbnailChange(file);
      else handleNoteFileChange(file);
    },
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      showToast("Note title is required.", 4000, "error");
      setError("Note title is required.");
      return;
    }
    if (!noteFile) {
      showToast("Please attach a note file before uploading.", 4000, "warn");
      return;
    }
    setError(null);
    setSuccess(false);
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("userId", currentUser._id);
      formData.append("notename", title.trim());
      if (desc.trim()) formData.append("desc", desc.trim());
      if (thumbnail) formData.append("file", thumbnail);
      if (noteFile) formData.append("notefile", noteFile);

      const res = await publicRequest.post("/notes", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setTitle(""); setDesc(""); setThumbnail(null); setNoteFile(null);
      setSuccess(true);
      showToast("Note uploaded successfully!");
      if (onNoteCreated) onNoteCreated(res.data);
      if (onCancel) setTimeout(() => onCancel(), 800);
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to upload note. Please try again.";
      setError(msg);
      showToast(msg, 4000, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      {onCancel && (
        <CloseBtn type="button" onClick={onCancel} aria-label="Close">
          <IconX size="12px" />
        </CloseBtn>
      )}
      <CardTitle>Share a Note</CardTitle>

      <form onSubmit={handleSubmit}>
        <Label htmlFor="note-title">Title *</Label>
        <Input
          id="note-title"
          type="text"
          placeholder="Note title"
          value={title}
          onChange={(e) => { setTitle(e.target.value); setError(null); }}
          required
        />

        <Label htmlFor="note-desc">Description</Label>
        <Textarea
          id="note-desc"
          placeholder="What is this note about?"
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
        />

        <FileRow>
          <FilePickerGroup>
            <Label>Thumbnail</Label>
            <HiddenInput
              ref={thumbRef}
              type="file"
              accept="image/*"
              onChange={(e) => handleThumbnailChange(e.target.files[0] || null)}
            />
            <FilePickerBtn
              type="button"
              $hasFile={!!thumbnail}
              $dragging={thumbDragging}
              onClick={() => thumbRef.current.click()}
              {...makeDragHandlers("image", setThumbDragging)}
            >
              {thumbnail ? <IconCheck size="12px" /> : <IconCamera size="12px" />}
              {thumbnail ? thumbnail.name : thumbDragging ? "Drop image here…" : "Choose or drop image…"}
            </FilePickerBtn>
          </FilePickerGroup>

          <FilePickerGroup>
            <Label>Note file</Label>
            <HiddenInput
              ref={fileRef}
              type="file"
              accept=".pdf,.doc,.docx,.ppt,.pptx,.txt"
              onChange={(e) => handleNoteFileChange(e.target.files[0] || null)}
            />
            <FilePickerBtn
              type="button"
              $hasFile={!!noteFile}
              $dragging={fileDragging}
              onClick={() => fileRef.current.click()}
              {...makeDragHandlers("file", setFileDragging)}
            >
              {noteFile ? <IconCheck size="12px" /> : <IconPaperclip size="12px" />}
              {noteFile ? noteFile.name : fileDragging ? "Drop file here…" : "Choose or drop file…"}
            </FilePickerBtn>
          </FilePickerGroup>
        </FileRow>

        <SubmitButton type="submit" disabled={loading}>
          {loading ? "Uploading…" : "Upload Note"}
        </SubmitButton>
      </form>

      {error && <StatusMsg $error>{error}</StatusMsg>}
      {success && <StatusMsg>Note uploaded successfully!</StatusMsg>}
    </Card>
  );
}
